/* eslint-disable @stylistic/max-len */
import type {RefObject} from 'react';

/**
 * The `DrinkingFountainIconProps` interface defines the properties for the DrinkingFountainIcon component.
 */
interface DrinkingFountainIconProps {
    /** CSS class to override default styling. */
    className?: string;
    /** Primary color of the icon. */
    color1?: string;
    /** Height of the icon. */
    height?: number | string;
    ref?: RefObject<SVGSVGElement>;
    /** Test ID for Cypress. */
    testId?: string;
    /** Width of the icon. */
    width?: number | string;
}

/**
 * Water-drop pictogram, used to mark "Trinkbrunnen" (drinking water points) on the map.
 * Kept as its own component (rather than reusing `WaterDropIcon`) since it represents a
 * different concept — a drinking-fountain point of interest, not the "Waterways" tour category.
 *
 * @param props           The component props.
 * @param props.className CSS class override.
 * @param props.color1    Primary icon color.
 * @param props.height    Icon height.
 * @param props.ref       SVG element ref.
 * @param props.testId    Test identifier.
 * @param props.width     Icon width.
 * @returns A React SVG element.
 */
export const DrinkingFountainIcon = ({
    className = '',
    color1 = 'currentColor',
    height,
    ref,
    testId = 'DrinkingFountainIcon',
    width
}: DrinkingFountainIconProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        fill={color1}
        height={height}
        viewBox="0 -960 960 960"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M489.85-206.15q10.46-1 17.23-7.77t6.77-16.46q0-11.31-7.47-17.89-7.46-6.58-18.76-5.58-41 3-89.31-24.03Q350-304.92 338-371.92q-2-10.23-8.58-16.08-6.57-5.85-15.65-5.85-10.92 0-18.19 8.2-7.27 8.19-4.66 21.03 16.23 87.16 78.47 124.62 62.23 37.46 120.46 33.85Zm-224.12 17.92Q180-276.46 180-408q0-93.08 74.5-204.04T480-853.84Q631-723 705.5-612.04 780-501.08 780-408q0 131.54-85.73 219.77Q608.54-100 480-100q-128.54 0-214.27-88.23ZM652-230.5Q720-301 720-408q0-73-60.5-165T480-774Q361-665 300.5-573T240-408q0 107 68 177.5T480-160q104 0 172-70.5ZM480-476.92Z" />
    </svg>
);

DrinkingFountainIcon.displayName = 'DrinkingFountainIcon';